import React, { useState, useEffect } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import "./SiteComparison.css";

const API = process.env.REACT_APP_API_URL;

const SITES = [
  { id: 5,  key: "p006", label: "Planter006", color: "#3b82f6" },
  { id: 11, key: "p007a", label: "Planter007A", color: "#10b981" },
];

const SEASON_ORDER  = ["DJF", "MAM", "JJA", "SON"];
const SEASON_LABELS = { DJF: "Winter", MAM: "Spring", JJA: "Summer", SON: "Autumn" };

function fmtMonth(m) {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(+y, +mo - 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
}

// ── Stat tiles ────────────────────────────────────────────────────────────────

function StatRow({ label, a, b, unit = "" }) {
  return (
    <div className="sc-stat-row">
      <span className="sc-stat-label">{label}</span>
      <span className="sc-stat-val" style={{ color: SITES[0].color }}>{a ?? "—"}{unit}</span>
      <span className="sc-stat-val" style={{ color: SITES[1].color }}>{b ?? "—"}{unit}</span>
    </div>
  );
}

function StatsCard({ a, b }) {
  return (
    <div className="sc-card">
      <div className="sc-card-header">
        <div className="sc-card-title">Lifetime Summary</div>
        <div className="sc-site-labels">
          <span style={{ color: SITES[0].color }}>{SITES[0].label}</span>
          <span style={{ color: SITES[1].color }}>{SITES[1].label}</span>
        </div>
      </div>
      <StatRow label="Events detected"    a={a?.event_count}         b={b?.event_count} />
      <StatRow label="Mean efficiency"    a={a?.mean_efficiency_pct} b={b?.mean_efficiency_pct} unit="%" />
      <StatRow label="Peak efficiency"    a={a?.peak_efficiency_pct} b={b?.peak_efficiency_pct} unit="%" />
      <StatRow label="Months monitored"   a={a?.months_monitored}    b={b?.months_monitored} />
      <StatRow label="First event"        a={a?.first_event}         b={b?.first_event} />
      <StatRow label="Latest event"       a={a?.last_event}          b={b?.last_event} />
    </div>
  );
}

// ── Seasonal comparison ───────────────────────────────────────────────────────

function SeasonalComparison({ a, b }) {
  if (!a?.length || !b?.length) return null;

  const bySeasonA = Object.fromEntries(a.map(r => [r.season, r]));
  const bySeasonB = Object.fromEntries(b.map(r => [r.season, r]));

  const data = SEASON_ORDER.map(s => ({
    label: SEASON_LABELS[s] || s,
    p006_count:  bySeasonA[s]?.event_count         ?? 0,
    p007a_count: bySeasonB[s]?.event_count         ?? 0,
    p006_eff:    bySeasonA[s]?.mean_efficiency_pct ?? null,
    p007a_eff:   bySeasonB[s]?.mean_efficiency_pct ?? null,
  }));

  return (
    <div className="sc-card">
      <div className="sc-card-header">
        <div className="sc-card-title">Seasonal Breakdown</div>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={data} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="count" orientation="left"  tick={{ fontSize: 10 }}
            label={{ value: "Events", angle: -90, position: "insideLeft", style: { fontSize: 10 } }} />
          <YAxis yAxisId="eff"   orientation="right" domain={[0, 100]} tick={{ fontSize: 10 }}
            label={{ value: "Mean eff. %", angle: 90, position: "insideRight", style: { fontSize: 10 } }} />
          <Tooltip formatter={(v, name) => {
            if (name === "p006_count")  return [v, "P006 events"];
            if (name === "p007a_count") return [v, "P007A events"];
            if (name === "p006_eff")    return [`${v}%`, "P006 mean eff."];
            if (name === "p007a_eff")   return [`${v}%`, "P007A mean eff."];
            return [v, name];
          }} />
          <Legend formatter={n => ({
            p006_count: "P006 events", p007a_count: "P007A events",
            p006_eff: "P006 mean eff.", p007a_eff: "P007A mean eff.",
          }[n] || n)} />
          <Bar yAxisId="count" dataKey="p006_count"  fill={SITES[0].color} radius={[3,3,0,0]} />
          <Bar yAxisId="count" dataKey="p007a_count" fill={SITES[1].color} radius={[3,3,0,0]} />
          <Line yAxisId="eff" dataKey="p006_eff"  stroke={SITES[0].color} strokeWidth={2} dot={{ r: 4 }} connectNulls />
          <Line yAxisId="eff" dataKey="p007a_eff" stroke={SITES[1].color} strokeWidth={2} dot={{ r: 4 }} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── SM trend at 30 cm ─────────────────────────────────────────────────────────

function SMTrendComparison({ a, b }) {
  if (!a?.length && !b?.length) return null;

  const monthMap = {};
  (a || []).forEach(r => {
    if (!monthMap[r.month]) monthMap[r.month] = { month: r.month, label: fmtMonth(r.month) };
    monthMap[r.month].p006_sm30 = r.sm_30 ?? null;
  });
  (b || []).forEach(r => {
    if (!monthMap[r.month]) monthMap[r.month] = { month: r.month, label: fmtMonth(r.month) };
    monthMap[r.month].p007a_sm30 = r.sm_30 ?? null;
  });
  const data = Object.values(monthMap).sort((x, y) => x.month.localeCompare(y.month));

  return (
    <div className="sc-card">
      <div className="sc-card-header">
        <div className="sc-card-title">Soil Moisture Trend — 30 cm depth</div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis domain={["auto", "auto"]} tick={{ fontSize: 10 }}
            label={{ value: "SM %", angle: -90, position: "insideLeft", style: { fontSize: 10 } }} />
          <Tooltip formatter={(v, name) => [
            v !== null && v !== undefined ? `${v}%` : "—",
            name === "p006_sm30" ? "P006 30 cm" : "P007A 30 cm",
          ]} />
          <Line dataKey="p006_sm30"  stroke={SITES[0].color} strokeWidth={2} dot={false} connectNulls name="p006_sm30" />
          <Line dataKey="p007a_sm30" stroke={SITES[1].color} strokeWidth={2} dot={false} connectNulls name="p007a_sm30" />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="sc-legend">
        {SITES.map(s => (
          <span key={s.key} className="sc-key">
            <span className="sc-dot" style={{ background: s.color }} />{s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── EC trend at 30 cm ─────────────────────────────────────────────────────────

function ECTrendComparison({ a, b }) {
  if (!a?.length && !b?.length) return null;

  const monthMap = {};
  (a || []).forEach(r => {
    if (!monthMap[r.month]) monthMap[r.month] = { month: r.month, label: fmtMonth(r.month) };
    monthMap[r.month].p006_ec30 = r.ec_30 ?? null;
  });
  (b || []).forEach(r => {
    if (!monthMap[r.month]) monthMap[r.month] = { month: r.month, label: fmtMonth(r.month) };
    monthMap[r.month].p007a_ec30 = r.ec_30 ?? null;
  });
  const data = Object.values(monthMap).sort((x, y) => x.month.localeCompare(y.month));

  return (
    <div className="sc-card">
      <div className="sc-card-header">
        <div className="sc-card-title">EC Trend — 30 cm depth</div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis domain={["auto", "auto"]} tick={{ fontSize: 10 }}
            label={{ value: "EC (VIC)", angle: -90, position: "insideLeft", style: { fontSize: 10 } }} />
          <Tooltip formatter={(v, name) => [
            v !== null && v !== undefined ? v : "—",
            name === "p006_ec30" ? "P006 30 cm" : "P007A 30 cm",
          ]} />
          <Line dataKey="p006_ec30"  stroke={SITES[0].color} strokeWidth={2} dot={false} connectNulls name="p006_ec30" />
          <Line dataKey="p007a_ec30" stroke={SITES[1].color} strokeWidth={2} dot={false} connectNulls name="p007a_ec30" />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="sc-legend">
        {SITES.map(s => (
          <span key={s.key} className="sc-key">
            <span className="sc-dot" style={{ background: s.color }} />{s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function SiteComparison() {
  const [cumA, setCumA]         = useState(null);
  const [cumB, setCumB]         = useState(null);
  const [seasonA, setSeasonA]   = useState([]);
  const [seasonB, setSeasonB]   = useState([]);
  const [smA, setSmA]           = useState([]);
  const [smB, setSmB]           = useState([]);
  const [ecA, setEcA]           = useState([]);
  const [ecB, setEcB]           = useState([]);

  useEffect(() => {
    const g = (url, set) =>
      fetch(url).then(r => r.json()).then(set).catch(() => {});

    g(`${API}/hydro/cumulative?site_id=5`,  setCumA);
    g(`${API}/hydro/cumulative?site_id=11`, setCumB);
    g(`${API}/hydro/seasonal?site_id=5`,    setSeasonA);
    g(`${API}/hydro/seasonal?site_id=11`,   setSeasonB);
    g(`${API}/hydro/sm-trend?site_id=5`,    setSmA);
    g(`${API}/hydro/sm-trend?site_id=11`,   setSmB);
    g(`${API}/hydro/ec-trend?site_id=5`,    setEcA);
    g(`${API}/hydro/ec-trend?site_id=11`,   setEcB);
  }, []);

  return (
    <div className="sc-page">
      <div className="sc-page-header">
        <h2 className="sc-page-title">Site Comparison</h2>
        <div className="sc-site-badge-row">
          {SITES.map(s => (
            <span key={s.id} className="sc-site-badge" style={{ borderColor: s.color, color: s.color }}>
              {s.label}
            </span>
          ))}
        </div>
      </div>

      <StatsCard a={cumA} b={cumB} />
      <SeasonalComparison a={seasonA} b={seasonB} />
      <SMTrendComparison a={smA} b={smB} />
      <ECTrendComparison a={ecA} b={ecB} />
    </div>
  );
}
